import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { faRotateRight } from '@fortawesome/pro-solid-svg-icons/faRotateRight';
import type { RolloutDayKey } from 'fym-shared/src/music24/releaseRollout';
import { TypedMemo } from '../../../../../common/types';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import { useSMLRouter } from '../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../routing/sml/SMLRoutes';
import { platformName } from '../../../platformMeta';
import { isSocialPlatform } from '../../../types';
import { StudioActionError } from '../../../studio/StudioActionError';
import { reelsPlanMessages } from '../reelsPlan/reelsPlanMessages';
import { reelsStepWait } from '../reelsPlan/reelsPlanModel';
import { useRollout } from './RolloutContext';
import type { PlanStepView } from './rolloutModel';
import { rolloutMessages } from './rolloutMessages';
import { COUNTDOWN_KEYS } from './useRolloutActions';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StepActionsProps {
  step: PlanStepView;
}

/**
 * The right side of a step card (design rollout-plan a1, b1, e1): its state and its one action. A
 * step of an AI reels plan (f3) has "Make it again" for a failed reel and the render bar while its
 * reel is made; its drafts come when the reel is ready, so it has no "Plan this step".
 */
const StepActionsComponent: React.FC<StepActionsProps> = ({ step }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { actions, canAct, creditsLeft, channels: usable, openStep, fits, plan } = useRollout();
  const isReels = plan.kind === 'reels';
  const reelsWait = isReels ? reelsStepWait(step) : null;
  const key = step.key as RolloutDayKey;
  const place = `step:${step.key}`;
  const isBusy = actions.busy === place;
  const disabled = !canAct || isBusy || actions.busy !== null;
  const feedback = actions.feedback[place] ?? null;
  const open = useCallback(() => openStep(step.key), [openStep, step.key]);
  const unplanned = step.channels.every((channel) => channel.state === 'notPlanned');
  const drafts = step.channels.filter(
    (channel) => channel.state === 'draft' && channel.post !== null && fits.get(channel.post.uuid)?.fits !== false
  );
  const failed = step.channels.find((channel) => channel.state === 'failed');
  const failedNeedsReconnect =
    failed !== undefined && usable.some((channel) => channel.platform === failed.platform && channel.needsReconnect);

  let content: React.ReactNode;
  if (reelsWait === 'failed') {
    content = (
      <>
        <SMLButton
          icon={<FontAwesomeIcon icon={faRotateRight} />}
          disabled={disabled}
          onClick={() => {
            actions.makeStepReel(place, key).catch(console.error);
          }}
        >
          {intl.formatMessage(reelsPlanMessages.makeAgain)}
        </SMLButton>
        <span className={styles.cost}>{intl.formatMessage(rolloutMessages.reelCost, { left: 'none' })}</span>
      </>
    );
  } else if (reelsWait !== null) {
    // The reel is made now, also for a step that has the drafts of the reel before it: no
    // "Schedule" until the new reel is ready.
    const percent = step.day.asset?.making ?? 0;
    content =
      reelsWait === 'making' ? (
        <span className={styles.reelBar} aria-hidden>
          <i className={styles.reelBarFill} style={{ width: `${Math.min(100, percent)}%` }} />
        </span>
      ) : null;
  } else if (step.assetState === 'missing' && unplanned) {
    if (step.day.asset?.themeId === 'aiReel') {
      content = (
        <>
          <SMLButton
            icon={<FontAwesomeIcon icon={faSparkles} />}
            disabled={disabled}
            onClick={() => {
              actions.makeStepReel(place, key).catch(console.error);
            }}
          >
            {intl.formatMessage(rolloutMessages.makeReel)}
          </SMLButton>
          <span className={styles.cost}>
            {intl.formatMessage(rolloutMessages.reelCost, {
              left: creditsLeft === null ? 'none' : String(creditsLeft),
            })}
          </span>
        </>
      );
    } else if (step.key === 'countdown3' || step.key === 'countdown1') {
      content = (
        <span className={clsx(styles.st, styles.stWk)}>{intl.formatMessage(rolloutMessages.needsTheCountdowns)}</span>
      );
    } else {
      const isCountdown = step.key === 'countdown7';
      content = (
        <>
          <SMLButton
            icon={<FontAwesomeIcon icon={faSparkles} />}
            disabled={disabled}
            onClick={() => {
              actions.makeAndPlan(place, isCountdown ? COUNTDOWN_KEYS : [key]).catch(console.error);
            }}
          >
            {intl.formatMessage(isCountdown ? rolloutMessages.makeCountdowns : rolloutMessages.makeIt)}
          </SMLButton>
          <span className={clsx(styles.cost, styles.costFree)}>{intl.formatMessage(rolloutMessages.freeImages)}</span>
        </>
      );
    }
  } else if (failed !== undefined) {
    content = failedNeedsReconnect ? (
      <SMLButton onClick={() => router.push(SMLRoutes.promoteChannels)}>
        {intl.formatMessage(rolloutMessages.reconnect)}
      </SMLButton>
    ) : (
      <button type="button" className={styles.link} onClick={() => openStep(step.key, failed.platform)}>
        {intl.formatMessage(rolloutMessages.open)}
      </button>
    );
  } else if (drafts.length > 0) {
    const [first] = drafts;
    const name = first !== undefined && isSocialPlatform(first.platform) ? platformName(first.platform) : '';
    content = (
      <>
        <SMLButton
          disabled={disabled}
          onClick={() => {
            actions
              .launch(
                place,
                drafts.map((channel) => channel.post?.uuid ?? '').filter((uuid) => uuid !== '')
              )
              .catch(console.error);
          }}
        >
          {drafts.length === 1
            ? intl.formatMessage(rolloutMessages.schedulePlatform, { name })
            : intl.formatMessage(rolloutMessages.scheduleDrafts, { count: drafts.length })}
        </SMLButton>
        <button type="button" className={styles.link} onClick={open}>
          {intl.formatMessage(rolloutMessages.open)}
        </button>
      </>
    );
  } else if (unplanned && step.assetState === 'ready' && !isReels && usable.length === 0) {
    // QA 2026-10-28 (imp-1): with no channel and no handle a draft has no place to go. The action
    // opens Channels, where the artist connects an account or types a handle.
    content = (
      <>
        <span className={clsx(styles.st, styles.stOk)}>{intl.formatMessage(rolloutMessages.assetReady)}</span>
        <button
          type="button"
          className={styles.link}
          disabled={!canAct}
          onClick={() => router.push(SMLRoutes.promoteChannels)}
        >
          {intl.formatMessage(rolloutMessages.addWherePost)}
        </button>
      </>
    );
  } else if (unplanned && step.assetState === 'ready' && !isReels) {
    content = (
      <>
        <span className={clsx(styles.st, styles.stOk)}>{intl.formatMessage(rolloutMessages.assetReady)}</span>
        <button
          type="button"
          className={styles.link}
          disabled={disabled}
          onClick={() => {
            actions.planDays(place, [key]).catch(console.error);
          }}
        >
          {intl.formatMessage(rolloutMessages.planStep)}
        </button>
      </>
    );
  } else {
    const allScheduled = step.channels.length > 0 && step.channels.every((channel) => channel.state === 'scheduled');
    content = (
      <>
        {allScheduled && (
          <span className={clsx(styles.st, styles.stSch)}>{intl.formatMessage(rolloutMessages.scheduled)}</span>
        )}
        {step.channels.some((channel) => channel.state === 'draft') && !allScheduled && drafts.length === 0 && (
          <span className={clsx(styles.st, styles.stDr)}>{intl.formatMessage(rolloutMessages.staysDraft)}</span>
        )}
        <button type="button" className={styles.link} onClick={open}>
          {intl.formatMessage(rolloutMessages.open)}
        </button>
      </>
    );
  }

  return (
    <div className={styles.right}>
      {content}
      <StudioActionError feedback={feedback} />
    </div>
  );
};

export const StepActions = TypedMemo(StepActionsComponent);
